import classNames from 'classnames'
import { FunctionComponent, useRef, useState } from 'react'
import { useEffectWithoutInitialRender } from '../../../hooks/useEffectWithoutInitialRender'
import { SortDirection } from '../../../types/SortDirection'
import { FOCUS_VISIBLE_RING } from '../../../lib/a11yFocus'
import { zIndex } from '../../../lib/zIndex'

export interface TableData {
	[key: string]: any
	_id: string
	_group?: TableData[]
}

export interface TableColumn {
	id: string
	label?: string
	sortable?: boolean
	columnClass?: string
	textAlign?: 'right' | 'center' | 'left'
	headerAlign?: 'right' | 'center' | 'left'
	renderer?(columnKey: string, data: TableData): JSX.Element
}

interface ISimpleSortTable {
	data: Array<TableData>
	columns: Array<TableColumn>
	defaultSorting?: {
		key: string
		direction: SortDirection
	}
	additionalClass?: string
	onSortPress?: (key: string, direction: string) => void
	isLoading?: boolean
	// Overrides the default row background/text classes, e.g. for a pinned total/summary row.
	rowClassName?: (row: TableData) => string
}

// QA fix item 8 (docs/design/fe-canonical-table-button-pattern.md - "horizontal scroll / sticky
// column / column resize" section): the minimum a dragged column can be squeezed down to, so a
// resize can never hide a column's content entirely.
const MIN_COLUMN_WIDTH = 80

const SimpleSortTable: FunctionComponent<ISimpleSortTable> = ({
	data,
	columns,
	defaultSorting,
	additionalClass,
	onSortPress,
	isLoading,
	rowClassName,
}) => {
	const defaultSortBy = defaultSorting?.key || columns[0].id
	const defaultDirection = defaultSorting?.direction || ('descending' as SortDirection)
	const [sortingBy, setSortingBy] = useState<string>(defaultSortBy)
	const [direction, setDirection] = useState<SortDirection>(defaultDirection)
	// Explicit per-column widths, set only once an admin drags a resize handle - undefined
	// columns keep their natural auto width. Lightweight native implementation (no drag/resize
	// library) per the QA fix item 8 decision.
	const [colWidths, setColWidths] = useState<Record<number, number>>({})
	const resizingRef = useRef<{ index: number; startX: number; startWidth: number } | null>(null)
	// Tracks whether the table is scrolled horizontally at all, so the sticky first column can
	// show a subtle divider border only while some other column's content is actually sliding
	// underneath it - not permanently, and not before there's anything to hide.
	const [isScrolledHorizontally, setIsScrolledHorizontally] = useState(false)

	const columnStyle = (index: number) => {
		const width = colWidths[index]
		const widthStyle = width ? { width, minWidth: width, maxWidth: width } : undefined
		if (index !== 0) return widthStyle
		return {
			...widthStyle,
			// Deliberately an inline style (not a Tailwind `z-10` class) - this must stay below the
			// page-level STICKY_HEADER tier so the sticky column slides underneath a sticky page
			// heading on vertical scroll, instead of rendering on top of it.
			zIndex: zIndex.TABLE_STICKY_COLUMN,
			// Promotes the sticky cell to its own stable compositor layer - without this, the
			// divider shadow (below) intermittently vanished/flickered during scroll because the
			// browser was re-rasterizing the seam between this layer and the non-promoted sibling
			// cell sliding underneath it on every frame.
			willChange: 'transform',
		}
	}

	const onTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
		setIsScrolledHorizontally(e.currentTarget.scrollLeft > 0)
	}

	const startResize = (index: number) => (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault()
		e.stopPropagation()
		const headerCell = (e.currentTarget as HTMLElement).parentElement
		const startWidth = colWidths[index] ?? headerCell?.getBoundingClientRect().width ?? 150
		resizingRef.current = { index, startX: e.clientX, startWidth }

		const onMouseMove = (moveEvent: MouseEvent) => {
			if (!resizingRef.current) {
				return
			}
			const delta = moveEvent.clientX - resizingRef.current.startX
			const newWidth = Math.max(MIN_COLUMN_WIDTH, resizingRef.current.startWidth + delta)
			setColWidths((prev) => ({ ...prev, [resizingRef.current!.index]: newWidth }))
		}
		const onMouseUp = () => {
			resizingRef.current = null
			document.removeEventListener('mousemove', onMouseMove)
			document.removeEventListener('mouseup', onMouseUp)
		}
		document.addEventListener('mousemove', onMouseMove)
		document.addEventListener('mouseup', onMouseUp)
	}

	const oppositeDirection = (direction: SortDirection) => {
		return direction === 'ascending' ? 'descending' : 'ascending'
	}

	const renderSortIcon = (key: string, activeKey: string, direction: SortDirection) => {
		if (activeKey === key) {
			// We are sorting by this key so should show an arrow
			if (direction === 'ascending') {
				// Ascending
				return <span className='fa-solid fa-arrow-up-short-wide text-charcoal-100 mr-2' />
			} else {
				// Descending
				return <span className='fa-solid fa-arrow-down-wide-short text-charcoal-100 mr-2' />
			}
		}
		// Otherwise show generic sorting icon
		return <span className='fa-solid fa-sort text-charcoal-300 mr-2' />
	}

	// Custom hook to skip the first render using the callback
	useEffectWithoutInitialRender(() => {
		onSortPress && onSortPress(sortingBy, direction)
	}, [sortingBy, direction])

	// Loading skeleton
	if (isLoading) {
		return (
			<div className={classNames('rounded-lg overflow-visible', additionalClass)}>
				<div className='animate-pulse bg-charcoal-800'>
					{/* Header skeleton */}
					<div
						className={`grid grid-cols-${columns.length} w-full bg-charcoal-800 text-charcoal-050`}
					>
						{columns.map((col, index) => (
							<div
								key={index}
								className={classNames(col.columnClass, 'my-3 mx-2 bg-charcoal-700 rounded-lg h-5')}
							></div>
						))}
					</div>
					{/* Row skeleton */}
					<div className={`grid grid-cols-${columns.length} w-full bg-charcoal-700 h-96`} />
				</div>
			</div>
		)
	}

	return (
		<div className={classNames('rounded-lg overflow-hidden', additionalClass)}>
			{/* overflow-x-auto (QA fix item 8): wide tables (many columns / narrow screens) now
			scroll horizontally within this box instead of clipping right-hand columns off-screen. */}
			<div className='overflow-x-auto' onScroll={onTableScroll}>
			<div className='table border-collapse w-full' role='table'>
				{/* Header row */}
				<div
					role='row'
					className='table-row w-full bg-charcoal-800 text-charcoal-050 border-b border-charcoal-050'
			>
				{columns.map((column, index) => (
					<div
						key={index}
						role='columnheader'
						// Explicit label, not left to content-based accessible-name computation - the
						// column-resize handle below is also labelled (aria-label="Resize <column>"), and
						// without this override its text would get folded into this header's own
						// accessible name (e.g. "Name Resize Name") since a columnheader with no explicit
						// name/label falls back to concatenating all of its descendants' text content.
						aria-label={column.label}
						style={columnStyle(index)}
						className={classNames('relative p-3 table-cell', column.columnClass, {
							// Sticky first column (QA fix item 8) - an explicit literal background (not
							// `bg-inherit`) is required here: CSS table layout's special background-painting
							// order doesn't reliably composite an inherited colour onto a `position: sticky`
							// cell, so it rendered transparent (other columns' text showed through underneath)
							// even though it was computed as opaque. Matches the header row's own bg-charcoal-800.
							// z-index comes from `columnStyle` (inline, see TABLE_STICKY_COLUMN) not a Tailwind class.
							'sticky left-0 bg-charcoal-800': index === 0,
						// Subtle divider while another column's content is actually hidden underneath - a
						// pseudo-element, not `border-r`: the table uses `border-collapse`, which resolves
						// adjacent-cell borders at layout time against the row's static grid, not this cell's
						// own sticky/promoted compositor layer, so a real border lags a frame behind on scroll
						// and only flashes into view at the extremes. A pseudo-element paints independently of
						// the table border model, so it stays in sync with the layer.
						"after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-charcoal-500 after:content-['']": index === 0 && isScrolledHorizontally,
						})}
					>
						{column.sortable ? (
							<button
							className={classNames(
								'flex w-full items-center rounded p-0.5',
								FOCUS_VISIBLE_RING,
								{
									'justify-start': (column.headerAlign || column.textAlign) === 'left' || (!column.headerAlign && !column.textAlign),
									'justify-end': (column.headerAlign || column.textAlign) === 'right',
									'justify-center': (column.headerAlign || column.textAlign) === 'center',
								}
							)}
								onClick={() => {
									if (sortingBy === column.id) {
										// Key is active, cycle sorting order
										if (oppositeDirection(direction) === 'ascending') {
											setDirection('ascending')
										} else {
											// Key has been through both directions, set to default column and order
											if (sortingBy === defaultSortBy) {
												// Exception to rule as we're already sorting default sort column so just flip direction
												setDirection(oppositeDirection(direction))
											} else {
												// Otherwise set sort to starting defaults
												setDirection(defaultDirection)
												setSortingBy(defaultSortBy)
											}
										}
									} else {
										// Key not active yet, so let's set it to the first sort direction and activate it
										setDirection('descending')
										setSortingBy(column.id)
									}
								}}
							>
								{renderSortIcon(column.id, sortingBy, direction)}
								<span className='text-xs'>{column.label}</span>
							</button>
						) : (
							<span
								className={classNames('flex text-xs', {
									'justify-start': (column.headerAlign || column.textAlign) === 'left',
									'justify-end': (column.headerAlign || column.textAlign) === 'right',
									'justify-center': (column.headerAlign || column.textAlign) === 'center',
									'w-full': (column.headerAlign || column.textAlign) !== void 0,
								})}
							>
								{column.label}
							</span>
						)}
						{/* Lightweight native column-resize handle (QA fix item 8) - no new dependency. */}
						<div
							onMouseDown={startResize(index)}
							role='separator'
							aria-orientation='vertical'
							aria-label={`Resize ${column.label || 'column'}`}
							className='absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none hover:bg-algae-500/50'
						/>
					</div>
				))}
			</div>

			{/* Data rows */}
			{data.length > 0 && (
				<>
					{/* For every row of data... */}
					{data.map((row, index) => {
						// Computed once per row (not just read from the row's own className) so the
						// sticky first cell below can carry the exact same literal background/text
						// classes - see the sticky-column comment above for why `bg-inherit` doesn't work.
						const rowBgClass = rowClassName ? rowClassName(row) : 'bg-charcoal-700 text-white'
						return (
							<div
								key={index}
								role='row'
								className={classNames(
									'table-row w-full border-b border-charcoal-050 last:border-b-0',
									rowBgClass
								)}
							>
								{/* And every column in the row... */}
								{columns.map((col, index2) => {
									return (
										<div
											key={index2}
											role='cell'
											style={columnStyle(index2)}
											className={classNames(
												'table-cell align-middle text-sm p-3',
												col.columnClass,
												{
													'text-right': col.textAlign === 'right',
													'text-center': col.textAlign === 'center',
													'text-left': col.textAlign === 'left',
												},
												{
													// Sticky first column (QA fix item 8) - see header comment above. z-index
													// comes from `columnStyle` (inline, see TABLE_STICKY_COLUMN).
													[classNames('sticky left-0', rowBgClass)]: index2 === 0,
												// Pseudo-element divider, not `border-r` - see header cell comment above for why.
												"after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-charcoal-500 after:content-['']": index2 === 0 && isScrolledHorizontally,
												}
											)}
										>
											{col.renderer ? col.renderer(col.id, row) : row[col.id]}
										</div>
									)
								})}
							</div>
						)
					})}
				</>
			)}
			</div>
			</div>
		</div>
	)
}

export default SimpleSortTable
